import React from 'react'
import styled from '@emotion/styled'

export type SlotRef = { refreshStyles: () => void }

type Props = {
  value: string
  isChanged: boolean
  /** Time in seconds */
  effectiveDuration: number
  /** Time in seconds */
  delay: number
  /** Time in seconds */
  speed: number
  /** Time in seconds */
  duration: number
  dummyList: (string | number)[]
  onFontHeightChange: (fontHeight: number) => void
  startValue?: string
  reverse?: boolean
}

const Slot = (
  {
    isChanged,
    effectiveDuration,
    delay,
    duration,
    speed,
    value,
    startValue,
    dummyList,
    reverse,
    onFontHeightChange,
  }: Props,
  ref: React.Ref<SlotRef>
) => {
  const numbersRef = React.useRef<HTMLSpanElement>(null)
  const valueRef = React.useRef(value)
  const itemRef = React.useRef<HTMLDivElement>(null)

  const [localValue, setLocalValue] = React.useState(value)
  const [fontHeight, setFontHeight] = React.useState(0)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dummyListState = React.useMemo(() => shuffle(dummyList), [dummyList, value])

  React.useLayoutEffect(() => {
    setFontHeight(itemRef.current?.getBoundingClientRect().height ?? 0)
  }, [])

  React.useEffect(() => {
    const numberElement = itemRef.current
    if (!fontHeight || !numberElement || !ResizeObserver) return
    const handleResize: ResizeObserverCallback = entries => {
      const height = entries[0].contentRect.height
      if (Math.abs(fontHeight - height) > 1) onFontHeightChange(height)
    }
    const observer = new ResizeObserver(handleResize)
    observer.observe(numberElement)
    return () => observer.disconnect()
  }, [fontHeight, onFontHeightChange])

  React.useEffect(() => {
    valueRef.current = value
    const timerId = setTimeout(
      () => setLocalValue(value),
      (effectiveDuration * speed * duration * 1000) / dummyList.length + delay * 1000
    )
    return () => clearTimeout(timerId)
  }, [value, effectiveDuration, delay, dummyList.length, speed, duration])

  React.useImperativeHandle(ref, () => ({
    refreshStyles: () => setFontHeight(itemRef.current?.getBoundingClientRect().height ?? 0),
  }))

  const slotNumbersHeight = fontHeight * (dummyList.length + 1)

  const topValue = reverse ? localValue : startValue ?? localValue
  const bottomValue = reverse ? startValue ?? localValue : localValue

  React.useEffect(() => {
    if (!numbersRef.current) return
    const el = numbersRef.current
    const animation = ['translateY(0px)', `translateY(-${slotNumbersHeight}px)`]

    el.style.transition = 'none'
    el.style.transform = reverse ? animation[1] : animation[0]

    if (isChanged) {
      el.animate(
        { transform: reverse ? animation.reverse() : animation },
        {
          fill: 'forwards',
          easing: 'ease-in-out',
          delay: delay * 1_000,
          duration: effectiveDuration * 1_000,
        }
      )
    }
  }, [isChanged, reverse, slotNumbersHeight, delay, effectiveDuration, value])

  return (
    <SlotStyled style={{ height: fontHeight }}>
      <NumbersStyled ref={numbersRef}>
        <NumbersStyled aria-hidden style={{ height: fontHeight }}>
          {topValue}
        </NumbersStyled>

        {dummyListState.map((dummyNumber, slotIndex) => (
          <NumbersStyled key={slotIndex} aria-hidden>
            {dummyNumber}
          </NumbersStyled>
        ))}

        <NumbersStyled ref={itemRef}>{bottomValue}</NumbersStyled>
      </NumbersStyled>
    </SlotStyled>
  )
}

export const SlotComponent = React.memo(React.forwardRef(Slot))

// =============
// Local Helpers
// =============

const shuffle = (arr: (string | number)[]) => {
  const result = [...arr]
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

// ======
// Styles
// ======

const SlotStyled = styled.span({
  display: 'inline-block',
  overflow: 'hidden',
  textAlign: 'center',
  verticalAlign: 'middle',
  willChange: 'transform',
})

const NumbersStyled = styled.span({
  display: 'block',
})
